import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PackagePlus, PackageMinus, Radio, Search } from 'lucide-react';
import { fleet, orgs } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { StatusDot } from '../components/StatusDot';
import { MetricCard } from '../components/MetricCard';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { TableSkeleton } from '../components/Skeleton';
import type { Organisation, UnassignedDevice } from '../types';

export default function CommissioningPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [selectedSerial, setSelectedSerial] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingEntry, setPendingEntry] = useState<{ org_id: string; serial: string; org_name: string } | null>(null);

  const { data: unassigned = [], isLoading: loadingUnassigned, error: unassignedError } = useQuery({
    queryKey: ['fleet', 'unassigned'],
    queryFn: fleet.listUnassigned,
    refetchInterval: 15_000,
  });

  const { data: organisations = [], isLoading: loadingOrgs, error: orgsError } = useQuery({
    queryKey: ['orgs'],
    queryFn: orgs.list,
  });

  const commissionMutation = useMutation({
    mutationFn: ({ orgId, serial }: { orgId: string; serial: string }) =>
      orgs.commissionDevice(orgId, serial),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet'] });
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
      setSelectedSerial(null);
      setSelectedOrgId('');
    },
  });

  const decommissionMutation = useMutation({
    mutationFn: ({ orgId, serial }: { orgId: string; serial: string }) =>
      orgs.decommissionDevice(orgId, serial),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet'] });
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
    },
  });

  const clientOrgs = organisations.filter(
    (o: Organisation) => o.org_type === 'client' || o.org_type === 'integrator',
  );

  const commissionedDevices = organisations.flatMap((o: Organisation) =>
    o.device_serials.map((serial) => ({
      serial,
      org_id: o.org_id,
      org_name: o.org_name,
    })),
  );

  const filteredUnassigned = unassigned.filter(
    (device: UnassignedDevice) =>
      searchFilter === '' ||
      device.serial.toLowerCase().includes(searchFilter.toLowerCase()) ||
      device.name.toLowerCase().includes(searchFilter.toLowerCase()),
  );

  const isLoading = loadingUnassigned || loadingOrgs;
  const error = (unassignedError || orgsError) as Error | null;

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400 mb-2">Failed to load commissioning data</p>
        <p className="text-xs text-gray-500">{error.message}</p>
        <button onClick={() => window.location.reload()} className="btn-primary mt-4 text-sm">
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-4">Commissioning</h2>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Unassigned"
          value={unassigned.length}
          accent="text-amber-400"
        />
        <MetricCard
          label="Commissioned"
          value={commissionedDevices.length}
          accent="text-emerald-400"
        />
        <MetricCard label="Client Orgs" value={clientOrgs.length} />
        <MetricCard label="Total Devices" value={unassigned.length + commissionedDevices.length} />
      </div>

      {/* Unassigned Devices table */}
      <div className="card overflow-hidden p-0 overflow-x-auto mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-3">
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <Radio size={16} className="text-amber-400" />
            Unassigned Devices
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search serial or name…"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="bg-surface-2 border border-surface-4 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-kinetica-500 w-48"
            />
          </div>
        </div>

        {isLoading ? (
          <table className="w-full">
            <tbody>
              <TableSkeleton columns={6} rows={5} />
            </tbody>
          </table>
        ) : filteredUnassigned.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 bg-surface-3 rounded-xl flex items-center justify-center mx-auto mb-3">
              <PackagePlus className="text-gray-500" size={24} />
            </div>
            <p className="text-sm font-medium text-gray-300">
              {searchFilter ? 'No devices match your search.' : 'No unassigned devices available.'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {searchFilter ? 'Try adjusting your search terms.' : 'New devices will appear here when they become available for commissioning.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Status</th>
                <th className="table-header table-cell text-left">Serial</th>
                <th className="table-header table-cell text-left">Name</th>
                <th className="table-header table-cell text-left">Firmware</th>
                <th className="table-header table-cell text-left">Last Seen</th>
                <th className="table-header table-cell text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUnassigned.map((device: UnassignedDevice) => {
                const isExpanded = selectedSerial === device.serial;
                return (
                  <tr key={device.serial} className="border-b border-surface-3 last:border-0">
                    <td className="table-cell">
                      <StatusDot status={device.status} showLabel />
                    </td>
                    <td className="table-cell font-mono text-xs text-kinetica-400">
                      {device.serial}
                    </td>
                    <td className="table-cell text-gray-200">{device.name}</td>
                    <td className="table-cell font-mono text-xs text-gray-500">
                      v{device.firmware_version}
                    </td>
                    <td className="table-cell text-xs text-gray-400">{device.last_seen}</td>
                    <td className="table-cell text-right">
                      {!isExpanded ? (
                        <button
                          onClick={() => {
                            setSelectedSerial(device.serial);
                            setSelectedOrgId('');
                          }}
                          className="text-xs text-kinetica-400 hover:text-kinetica-300 font-medium transition-colors"
                        >
                          Commission
                        </button>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <select
                            value={selectedOrgId}
                            onChange={(e) => setSelectedOrgId(e.target.value)}
                            className="bg-surface-2 border border-surface-4 rounded-lg px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-kinetica-500"
                          >
                            <option value="">Select organisation…</option>
                            {clientOrgs.map((o: Organisation) => (
                              <option key={o.org_id} value={o.org_id}>
                                {o.org_name}
                              </option>
                            ))}
                          </select>
                          <button
                            disabled={!selectedOrgId || commissionMutation.isPending}
                            onClick={() =>
                              commissionMutation.mutate({
                                orgId: selectedOrgId,
                                serial: device.serial,
                              })
                            }
                            className="text-xs text-emerald-400 hover:text-emerald-300 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => {
                              setSelectedSerial(null);
                              setSelectedOrgId('');
                            }}
                            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Commission error */}
        {commissionMutation.isError && (
          <div className="mx-4 mt-4 mb-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-xs text-red-400 font-medium mb-0.5">Commissioning failed</p>
            <p className="text-xs text-red-300/80">
              {(commissionMutation.error as Error).message}
            </p>
          </div>
        )}
      </div>

      {/* Commissioned Devices table */}
      {commissionedDevices.length > 0 && (
        <div className="card overflow-hidden p-0 overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-3 text-sm font-medium text-white">
            <PackageMinus size={16} className="text-emerald-400" />
            Commissioned Devices
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Serial</th>
                <th className="table-header table-cell text-left">Organisation</th>
                {user?.org_type === 'kinetica' && (
                  <th className="table-header table-cell text-right">Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {commissionedDevices.map((entry) => (
                <tr
                  key={`${entry.org_id}-${entry.serial}`}
                  className="border-b border-surface-3 last:border-0"
                >
                  <td className="table-cell font-mono text-xs text-kinetica-400">
                    {entry.serial}
                  </td>
                  <td className="table-cell text-xs text-gray-400">{entry.org_name}</td>
                  {user?.org_type === 'kinetica' && (
                    <td className="table-cell text-right">
                      <button
                        disabled={decommissionMutation.isPending}
                        onClick={() => {
                          setPendingEntry(entry);
                          setDialogOpen(true);
                        }}
                        className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Decommission
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={dialogOpen}
        title={`Decommission ${pendingEntry?.serial}?`}
        message={`This will remove ${pendingEntry?.serial} from ${pendingEntry?.org_name} and return it to the unassigned pool. This action cannot be undone.`}
        severity="danger"
        confirmLabel="Decommission"
        loading={decommissionMutation.isPending}
        onConfirm={() => {
          if (!pendingEntry) return;
          decommissionMutation.mutate(
            { orgId: pendingEntry.org_id, serial: pendingEntry.serial },
            {
              onSettled: () => {
                setDialogOpen(false);
                setPendingEntry(null);
              },
            }
          );
        }}
        onCancel={() => {
          setDialogOpen(false);
          setPendingEntry(null);
        }}
      />
    </div>
  );
}
